import { useCallback, useEffect, useState } from 'react';
import { apiGet, ApiError } from './apiClient.js';

export type ApiGetState<T> =
  | { readonly status: 'loading'; readonly reload: () => void }
  | { readonly status: 'error'; readonly error: ApiError; readonly reload: () => void }
  | { readonly status: 'ready'; readonly data: T; readonly reload: () => void };

/** Shared GET fetch-on-mount hook — one loading/error/reload shape for every page. */
export function useApiGet<T>(path: string | null, deps: readonly unknown[]): ApiGetState<T> {
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const [state, setState] = useState<
    | { readonly status: 'loading' }
    | { readonly status: 'error'; readonly error: ApiError }
    | { readonly status: 'ready'; readonly data: T }
  >({ status: 'loading' });

  useEffect(() => {
    if (path === null) return;
    let cancelled = false;
    setState({ status: 'loading' });
    apiGet<T>(path)
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const apiError =
          error instanceof ApiError
            ? error
            : new ApiError(0, {
                error: { code: 'INTERNAL', message: 'Network error', requestId: '' },
              });
        setState({ status: 'error', error: apiError });
      });
    return () => {
      cancelled = true;
    };
    // deps is the caller's explicit dependency list — path and nonce are always included.
  }, [path, nonce, ...deps]);

  return { ...state, reload };
}
